import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { api, errMsg, useApi, useRefresh } from "@/lib/api";
import { TEMPLATE_VARS } from "@/lib/crm";

const SAMPLE = { nome: "Mario", cognome: "Rossi", prodotto: "Mutuo", importo: "€ 150.000", data_richiamo: "15/07/2026 10:00" };
const preview = (t) => TEMPLATE_VARS.reduce((s, v) => s.replaceAll(`{{${v}}}`, SAMPLE[v]), t || "");

export default function Templates() {
  const { data: tData } = useApi("/templates");
  const data = useMemo(() => tData || [], [tData]);
  const refresh = useRefresh();
  const [sel, setSel] = useState(null);
  const [f, setF] = useState({ name: "", subject: "", body: "" });
  const bodyRef = useRef(null);

  useEffect(() => { if (!sel && data.length) setSel(data[0].template_id); }, [data, sel]);
  useEffect(() => {
    const t = data.find((x) => x.template_id === sel);
    setF(t ? { name: t.name, subject: t.subject, body: t.body } : { name: "", subject: "", body: "" });
  }, [sel, data]);

  const insertVar = (v) => {
    const el = bodyRef.current, tag = `{{${v}}}`;
    const pos = el?.selectionStart ?? f.body.length;
    setF({ ...f, body: f.body.slice(0, pos) + tag + f.body.slice(pos) });
  };

  const save = async () => {
    if (!f.name.trim() || !f.body.trim()) return toast.error("Nome e testo obbligatori");
    try {
      if (sel === "new") { const { data: t } = await api.post("/templates", f); setSel(t.template_id); }
      else await api.put(`/templates/${sel}`, f);
      toast.success("Modello salvato");
      refresh();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async () => { await api.delete(`/templates/${sel}`); setSel(null); refresh(); toast.success("Modello eliminato"); };

  return (
    <div className="space-y-6 fade-up" data-testid="templates-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Modelli messaggi</h1>
          <p className="text-slate-500 mt-1">Testi pronti per WhatsApp, SMS ed email, con variabili automatiche</p>
        </div>
        <button data-testid="add-template-btn" onClick={() => setSel("new")} className="flex items-center gap-2 rounded-full bg-[#0F172A] text-white px-5 py-2.5 text-sm font-medium hover:bg-sky-600 transition-colors"><Plus className="h-4 w-4" />Nuovo modello</button>
      </div>
      <div className="grid lg:grid-cols-[280px_1fr] gap-6">
        <div className="bg-white border border-slate-200/80 rounded-xl overflow-hidden self-start">
          {data.map((t) => (
            <button key={t.template_id} data-testid={`template-item-${t.template_id}`} onClick={() => setSel(t.template_id)}
              className={`w-full text-left px-4 py-3 border-b border-slate-100 last:border-0 text-sm transition-colors ${sel === t.template_id ? "bg-sky-50 text-sky-800 font-medium" : "hover:bg-slate-50"}`}>{t.name}</button>
          ))}
          {sel === "new" && <div className="px-4 py-3 text-sm bg-sky-50 text-sky-800 font-medium">Nuovo modello…</div>}
        </div>
        {sel && (
          <div className="grid xl:grid-cols-2 gap-6">
            <div className="bg-white border border-slate-200/80 rounded-xl p-5 space-y-4">
              <div className="space-y-1.5"><Label className="text-xs text-slate-500">Nome</Label><Input data-testid="template-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
              <div className="space-y-1.5"><Label className="text-xs text-slate-500">Oggetto email</Label><Input data-testid="template-subject" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} /></div>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500">Testo</Label>
                <Textarea data-testid="template-body" ref={bodyRef} rows={9} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} />
                <div className="flex flex-wrap gap-1.5">
                  {TEMPLATE_VARS.map((v) => <button key={v} type="button" data-testid={`template-var-${v}`} onClick={() => insertVar(v)} className="font-mono text-[11px] px-2 py-1 rounded-md bg-slate-100 hover:bg-sky-100 text-slate-700">{`{{${v}}}`}</button>)}
                </div>
              </div>
              <div className="flex gap-2">
                <Button data-testid="template-save-btn" onClick={save} className="bg-[#0F172A] hover:bg-sky-600">Salva</Button>
                {sel !== "new" && <Button data-testid="template-delete-btn" variant="outline" onClick={remove} className="text-rose-600"><Trash2 className="h-4 w-4 mr-1" />Elimina</Button>}
              </div>
            </div>
            <div className="rounded-xl bg-[#0F172A] p-5 self-start">
              <p className="text-xs uppercase tracking-wider text-slate-400 mb-3">Anteprima (cliente di esempio)</p>
              <div className="bg-[#DCF8C6] text-slate-900 rounded-xl rounded-tr-none p-4 text-sm whitespace-pre-wrap shadow" data-testid="template-preview">{preview(f.body) || "…"}</div>
              {f.subject && <p className="text-slate-300 text-sm mt-4"><span className="text-slate-500">Oggetto:</span> {preview(f.subject)}</p>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
